import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Package, ShieldCheck, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ProductCard } from '@/components/ProductCard';
import { track } from '@/lib/analytics';

// ~1.2 cards on phones, 3 on tablets, 5 on desktop
const SLIDE = 'w-[80%] flex-none snap-start md:w-[calc((100%-2*1.25rem)/3)] xl:w-[calc((100%-4*1.25rem)/5)]';

const ArrowButton = ({ side, label, disabled, onClick }) => {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={`bears-carousel-${side === 'left' ? 'prev' : 'next'}`}
      className={`absolute top-[38%] z-10 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-candy-ink text-white shadow-[0_10px_24px_-10px_rgb(var(--ink)/0.6)] transition-opacity duration-150 hover:bg-candy-ink/90 disabled:pointer-events-none disabled:opacity-0 md:grid ${
        side === 'left' ? '-left-3 xl:-left-6' : '-right-3 xl:-right-6'
      }`}
    >
      <Icon className="h-6 w-6" aria-hidden="true" />
    </button>
  );
};

export const BearsCollection = ({ products, loading, onAddToCart }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const tracked = useRef(false);

  // view_item_list once the collection scrolls into view
  useEffect(() => {
    const el = ref.current;
    if (!el || products.length === 0 || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !tracked.current) {
        tracked.current = true;
        track('view_item_list', { item_list_id: 'bears', items: products.map((p) => ({ item_id: p.product_id })) });
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [products]);

  // Carousel: native scroll-snap (swipe / trackpad), arrows from md up
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, products.length, loading]);
  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const perks = [
    { icon: Truck, label: t('bears.perkShipping') },
    { icon: Package, label: t('bears.perkPackaging') },
    { icon: ShieldCheck, label: t('bears.perkIngredients') },
  ];

  return (
    <section
      id="bears"
      ref={ref}
      aria-labelledby="bears-title"
      className="mx-auto max-w-content px-4 pb-6 pt-6 md:px-6 md:pt-9"
      data-testid="featured-products-section"
    >
      <h2 id="bears-title" className="sr-only">{t('bears.title')}</h2>

      <div className="relative">
        <div
          ref={scrollerRef}
          onScroll={updateEdges}
          className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 px-4 pb-1 md:mx-0 md:scroll-px-0 md:px-0 md:gap-5"
          data-testid="bears-carousel"
        >
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`${SLIDE} aspect-[3/4.4] animate-pulse rounded-card bg-candy-surface`} aria-hidden="true" />
              ))
            : products.map((product, i) => (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  onAddToCart={onAddToCart}
                  priority={i < 3}
                  listName="bears"
                  sizes="(min-width: 1280px) 250px, (min-width: 768px) 33vw, 80vw"
                  className={SLIDE}
                />
              ))}
        </div>

        {!(edges.start && edges.end) && (
          <>
            <ArrowButton side="left" label={t('bears.prev')} disabled={edges.start} onClick={() => scrollByPage(-1)} />
            <ArrowButton side="right" label={t('bears.next')} disabled={edges.end} onClick={() => scrollByPage(1)} />
          </>
        )}
      </div>

      <ul className="mt-[22px] flex flex-wrap justify-center gap-x-7 gap-y-2 text-[15px] font-bold">
        {perks.map(({ icon: Icon, label }) => (
          <li key={label} className="flex items-center gap-2">
            <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            {label}
          </li>
        ))}
      </ul>
    </section>
  );
};
